import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  Form,
  Pagination,
} from "react-bootstrap";
import { FaPlus, FaCalendarAlt } from "react-icons/fa";
import { MdOutlineClass } from "react-icons/md";
import { FaRegUser } from "react-icons/fa6";
import { FaRegAddressBook, FaGlobe, FaWhatsapp, FaShareAlt, FaCheckCircle } from "react-icons/fa";
import { MdMarkEmailRead } from "react-icons/md";
import axios from "axios";

import NavMenu from "./NavMenu";
import { useTheme } from "../components/ThemeContext";
import { formatPhoneForDisplay } from "../utils/formatPhone";

import Avatar from "./Avatar";
import { accessibleBg } from "../utils/color";
import "./dashboard.css";

import noDataImg from "../img/no_data.svg";

const API = process.env.REACT_APP_API || "";
const ENDPOINT_GET_SOURCES = `${API}/getSources`;

// One dashboard stat tile: tinted icon badge + title, a big number, an
// optional "% replied" bar, and a footer of secondary figures. Defined at
// module scope so it isn't re-created on every render (the page re-renders
// every second for the clock).
const StatCard = ({ accent, icon, title, tooltip, label, value, valueClass, items, isDark, delay }) => (
  <Card
    className={`tmis-stat dash-stat dash-fade ${accent} ${isDark ? "dark" : ""}`}
    style={{ "--d": `${delay}ms` }}
  >
    <div className="tmis-stat-icon-bg" aria-hidden="true">
      {icon}
    </div>
    <Card.Body className="tmis-stat-body">
      <div className="tmis-stat-top">
        <span className="dash-stat-icon">{icon}</span>
        <div className="tmis-stat-title" title={tooltip}>
          {title}
        </div>
      </div>

      <div className="tmis-stat-hero">
        <div className="tmis-stat-hero-label">{label}</div>
        <div className={`tmis-stat-hero-value ${valueClass || ""}`}>{value}</div>
      </div>

      <div className="tmis-stat-foot">
        {items.map((it) => (
          <span key={it.label}>
            {it.label}{" "}
            <strong className={it.className}>{it.value}</strong>
          </span>
        ))}
      </div>
    </Card.Body>
  </Card>
);

export default function Dashboard() {
  const { isDark } = useTheme();

  const [user, setUser] = useState({ user_name: "", id: "" });
  const [now, setNow] = useState(new Date());

  const [enquires, setEnquires] = useState([]);
  const [enquireCounts, setEnquireCounts] = useState({});
  const [activities, setActivities] = useState([]);
  const [activityRange, setActivityRange] = useState(4);
  const [sourceList, setSourceList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    if (loggedInUser) setUser(loggedInUser);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // const saveWhatsappToEnquiry = async () => {
  //   try {
  //     setSavingWhatsapp(true);

  //     const res = await axios.post(
  //       `${process.env.REACT_APP_API}/saveWhatsappEnquire`,
  //     );

  //     alert(res.data.message);

  //     await Promise.all([
  //       fetchEnquires(),
  //       fetchEnquireCounts(),
  //       fetchActivities(activityRange),
  //     ]);

  //     setLastRefreshTime(new Date());
  //   } catch (err) {
  //     alert(err.response?.data?.message || "Failed to save WhatsApp data.");
  //   } finally {
  //     setSavingWhatsapp(false);
  //   }
  // };

  const fetchEnquires = async () => {
    const res = await fetch(`${process.env.REACT_APP_API}/getEnquires`);
    const json = await res.json();
    if (json?.success) setEnquires(json.data || []);
  };

  const fetchEnquireCounts = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/getEnquiresCount`,
    );
    if (res.data?.success) setEnquireCounts(res.data.data || {});
  };

  const fetchActivities = async (days) => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/getActivityLogs?days=${days}`,
    );
    if (res.data?.success) setActivities(res.data.data || []);
  };

  const fetchSources = async () => {
    try {
      const res = await axios.get(ENDPOINT_GET_SOURCES);
      if (res.data?.success) setSourceList(res.data.data || []);
    } catch (err) {
      console.error("getSources error:", err);
    }
  };

  useEffect(() => {
    const refreshData = async () => {
      await Promise.all([
        fetchEnquires(),
        fetchEnquireCounts(),
        fetchActivities(activityRange),
        fetchSources(),
      ]);
      setLastRefreshTime(new Date());
    };

    refreshData();

    const interval = setInterval(refreshData, 60000); // 1 minute
    return () => clearInterval(interval);
  }, [activityRange]);

  const formatTime = (date) =>
    date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  // Date and time are returned separately so the table can stack them
  // (date on top, time muted underneath) instead of one long string.
  const formatDateParts = (dateTimeStr) => {
    if (!dateTimeStr) return { date: "-", time: "" };
    const dt = new Date(dateTimeStr);
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    const ss = String(dt.getSeconds()).padStart(2, "0");
    return { date: `${dd}-${mm}-${yyyy}`, time: `${hh}:${mi}:${ss}` };
  };

  const isToday = (dateStr) => {
    const d = new Date(dateStr);
    const t = new Date();
    return (
      d.getDate() === t.getDate() &&
      d.getMonth() === t.getMonth() &&
      d.getFullYear() === t.getFullYear()
    );
  };

  const todaysEnquiries = useMemo(
    () => enquires.filter((x) => isToday(x.submission_date)),
    [enquires],
  );

  const totalPages = Math.ceil(todaysEnquiries.length / itemsPerPage);
  const paginatedEnquiries = todaysEnquiries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => setCurrentPage(1), [enquires]); // keep pagination sane

  const dateLine = now.toLocaleString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeLine = now.toLocaleString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const norm = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const normalizeHex = (c) => {
    const s = String(c || "").trim();
    if (!s) return "";
    return s.startsWith("#") ? s : `#${s}`;
  };

  // returns black/white text, whichever gives the higher WCAG contrast ratio
  // against the given background (guarantees >=4.5:1 for any color)
  const getTextColor = (bg) => {
    const hex = normalizeHex(bg).replace("#", "");
    if (hex.length !== 6) return "#ffffff";
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return "#ffffff";

    const toLinear = (c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const luminance =
      0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

    const contrastWithWhite = 1.05 / (luminance + 0.05);
    const contrastWithBlack = (luminance + 0.05) / 0.05;

    return contrastWithBlack >= contrastWithWhite ? "#111827" : "#ffffff";
  };

  // status colors stay fixed (not user-defined in Data Center)
  const getStatusColor = (status) => {
    switch (norm(status)) {
      case "walk-in":
        return "#3b82f6";
      case "email sent":
        return "#22c55e";
      case "responded":
        return "#0ea5e9";
      case "webenquiry":
        return "#f59e0b";
      case "ssg":
        return "#ef4444";
      case "phone call":
        return "#a855f7";
      case "pending":
        return "#FC6C62";
      default:
        return "#64748b";
    }
  };

  // Enquiry lifecycle status shown as a badge: Pending (no reply yet) /
  // Email Sent (replied over email) / Responded (replied over WhatsApp).
  const getStatusLabel = (status) => {
    const s = norm(status);
    if (s === "email sent") return "Email Sent";
    if (s === "responded") return "Responded";
    return "Pending";
  };

  // source colors are user-defined in Data Center and stored in the database
  const getSourceColor = (source) => {
    const found = sourceList.find((x) => norm(x.source) === norm(source));
    return accessibleBg(normalizeHex(found?.color) || "#6c757d");
  };

  const getBorderColor = (source) => getSourceColor(source);

  // Group activities by date label
  const activityDates = useMemo(() => {
    const set = new Set(
      activities.map((a) =>
        new Date(a.created_at).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
      ),
    );
    return [...set];
  }, [activities]);

  return (
    <Container
      fluid
      className={`min-vh-100 p-3 p-md-4 dash-page ${
        isDark ? "dark-theme" : "light-theme"
      }`}
    >
      <NavMenu />

      <Row className="g-3 mt-3 align-items-stretch">
        {/* LEFT: main column */}
        <Col lg={9} xs={12} className="d-flex flex-column gap-3">
          {/* HERO */}
          <div className="dash-hero dash-fade">
            <div className="dash-hero-text">
              <span className="dash-hero-chip">
                Welcome back <span>👋</span>
              </span>
              <h1 className="dash-hero-title">{user.user_name || "System Admin"}</h1>
              <p className="dash-hero-sub">
                Monitor new enquiries and follow-ups in one place.
              </p>
              <div className="dash-hero-stats">
                <span className="dash-hero-stat">
                  <b>{todaysEnquiries.length}</b> new today
                </span>
                <span className="dash-hero-stat">
                  <b>{enquireCounts?.email_unsent ?? 0}</b> awaiting reply
                </span>
              </div>
            </div>

            <div className="dash-clock">
              <div className="dash-clock-date">{dateLine}</div>
              <div className="dash-clock-time">{timeLine}</div>
            </div>
          </div>

          {/* STATS ROW - 5 across on desktop via CSS grid (Bootstrap's
              12-col grid doesn't divide evenly by 5) */}
          <div className="tmis-stats-grid">
            <StatCard
              isDark={isDark} delay={0} accent="accent-blue" icon={<FaGlobe />}
              title="Web Enquiry" tooltip="Submitted via online form"
              label="Today" value={enquireCounts?.webenquiry_today ?? 0}
              items={[
                { label: "This month", value: enquireCounts?.webenquiry_month ?? 0 },
                { label: "All time", value: enquireCounts?.webenquiry_all ?? 0 },
              ]}
            />
            <StatCard
              isDark={isDark} delay={60} accent="accent-violet" icon={<FaRegAddressBook />}
              title="Manual Enquiry" tooltip="Internal / recorded enquiries"
              label="Today" value={enquireCounts?.input_today ?? 0}
              items={[
                { label: "This month", value: enquireCounts?.input_month ?? 0 },
                { label: "All time", value: enquireCounts?.input_all ?? 0 },
              ]}
            />
            <StatCard
              isDark={isDark} delay={120} accent="accent-teal" icon={<MdMarkEmailRead />}
              title="Reply Status" tooltip="Follow-up replies, by channel"
              label="Pending" value={enquireCounts?.email_unsent ?? 0} valueClass="tmis-hero-red"
              items={[
                { label: "Email Sent", value: enquireCounts?.email_sent ?? 0, className: "tmis-foot-green" },
                { label: "Responded (WhatsApp)", value: enquireCounts?.responded_all ?? 0, className: "tmis-foot-green" },
              ]}
            />
            <StatCard
              isDark={isDark} delay={180} accent="accent-green" icon={<FaWhatsapp />}
              title="WhatsApp" tooltip="Enquiries received via WhatsApp"
              label="Today" value={enquireCounts?.whatsapp_today ?? 0}
              items={[
                { label: "This month", value: enquireCounts?.whatsapp_month ?? 0 },
                { label: "All time", value: enquireCounts?.whatsapp_all ?? 0 },
              ]}
            />
            <StatCard
              isDark={isDark} delay={240} accent="accent-magenta" icon={<FaShareAlt />}
              title="Social Media" tooltip="Facebook, Instagram & other social leads"
              label="Today" value={enquireCounts?.social_today ?? 0}
              items={[
                { label: "This month", value: enquireCounts?.social_month ?? 0 },
                { label: "All time", value: enquireCounts?.social_all ?? 0 },
              ]}
            />
          </div>

          <div className="dash-section-head dash-fade" style={{ "--d": "260ms" }}>
            <div>
              <div className="dash-section-title">
                <span className="dash-title-icon">
                  <FaRegAddressBook />
                </span>
                Today’s Enquiries
              </div>
              <div className="dash-refreshed">
                <span className="dash-live-dot" />
                Last refreshed at <strong>{formatTime(lastRefreshTime)}</strong>
              </div>
            </div>

            <span className="tmis-enq-badge">
              <FaPlus className="me-2" />
              {todaysEnquiries.length} Enquiries
            </span>
          </div>

          <Card
            className={`tmis-tablecard dash-fade ${isDark ? "dark" : ""}`}
            style={{ "--d": "320ms" }}
          >
            <div className="table-responsive">
              <Table
                hover
                className={`mb-0 align-middle dash-table tmis-table-responsive ${isDark ? "table-dark" : ""}`}
              >
                <thead className={isDark ? "table-dark" : ""}>
                  <tr>
                    <th style={{ width: 50 }}>No</th>
                    <th style={{ minWidth: 120 }}>Date</th>
                    <th style={{ minWidth: 170 }}>Name</th>
                    <th style={{ minWidth: 90 }} className="tmis-hide-md">
                      Phone
                    </th>
                    <th style={{ minWidth: 140 }} className="tmis-hide-lg">
                      Email
                    </th>
                    <th style={{ minWidth: 90 }} className="tmis-hide-md">
                      Country
                    </th>
                    <th style={{ minWidth: 110 }} className="tmis-hide-lg">
                      Details
                    </th>
                    <th style={{ minWidth: 110 }}>Source</th>
                    <th style={{ minWidth: 110 }}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedEnquiries.map((s, i) => {
                    const when = formatDateParts(s.submission_date);
                    return (
                      <tr key={s.id || i} className="tmis-row">
                        <td data-label="No">
                          {(currentPage - 1) * itemsPerPage + i + 1}
                        </td>

                        <td data-label="Date">
                          <div className="dash-date">{when.date}</div>
                          <div className="dash-time">{when.time}</div>
                        </td>

                        <td data-label="Name">
                          <div className="dash-person">
                            <Avatar name={s.name} size={34} />
                            <span className="dash-person-name">{s.name}</span>
                          </div>
                        </td>

                        <td data-label="Phone" className="tmis-hide-md">
                          {formatPhoneForDisplay(s.phone_number, s.country, s.source, s.conversation_id)}
                        </td>

                        <td
                          data-label="Email"
                          className="text-lowercase tmis-hide-lg"
                          title={s.email || undefined}
                        >
                          {s.email}
                        </td>

                        <td
                          data-label="Country"
                          className="text-capitalize tmis-hide-md"
                        >
                          {(s.country || "").toLowerCase()}
                        </td>

                        <td
                          data-label="Details"
                          className="tmis-ellipsis tmis-hide-lg"
                          title={s.enquiry || undefined}
                        >
                          {s.enquiry || "-"}
                        </td>

                        <td data-label="Source" className="text-capitalize">
                          <span
                            className="tmis-pill"
                            style={{
                              backgroundColor: getSourceColor(s.source),
                              color: getTextColor(getSourceColor(s.source)),
                            }}
                          >
                            {s.source || "-"}
                          </span>
                        </td>

                        <td data-label="Status">
                          <span
                            className="tmis-pill"
                            style={{
                              backgroundColor: getStatusColor(getStatusLabel(s.status)),
                              color: getTextColor(getStatusColor(getStatusLabel(s.status))),
                            }}
                          >
                            {getStatusLabel(s.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}

                  {paginatedEnquiries.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center py-5">
                        <div className="text-muted">
                          No enquiries for today.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-2">
              <Pagination size="sm" className="mb-0">
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                />

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  const isCurrent = page === currentPage;
                  const isNear = Math.abs(page - currentPage) <= 2;
                  const isEdge = page === 1 || page === totalPages;

                  if (isCurrent || isNear || isEdge) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={isCurrent}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  }

                  if (
                    (page === currentPage - 3 && page !== 2) ||
                    (page === currentPage + 3 && page !== totalPages - 1)
                  ) {
                    return (
                      <Pagination.Ellipsis key={`ellipsis-${page}`} disabled />
                    );
                  }

                  return null;
                })}

                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Col>

        {/* RIGHT: activities panel */}
        <Col lg={3} xs={12} className="d-flex">
          <Card
            className={`tmis-activities dash-fade ${isDark ? "dark" : ""}`}
            style={{ "--d": "200ms" }}
          >
            <Card.Header className="dash-activities-head">
              <div className="dash-activities-title">
                <span className="dash-title-icon">
                  <FaCalendarAlt />
                </span>
                Activities
              </div>

              <Form.Select
                size="sm"
                className={`tmis-compact-select ${isDark ? "dark" : ""}`}
                value={activityRange}
                onChange={(e) => setActivityRange(Number(e.target.value))}
                aria-label="Activity range"
              >
                <option value="1">1 Day</option>
                <option value="2">2 Days</option>
                <option value="3">3 Days</option>
                <option value="4">4 Days</option>
                <option value="5">5 Days</option>
              </Form.Select>
            </Card.Header>

            <Card.Body className="tmis-activities-body">
              {activities.length === 0 ? (
                <div className="tmis-empty">
                  <img
                    src={noDataImg}
                    alt="No Activity"
                    className="tmis-empty-img"
                  />
                  <div
                    className={`mt-3 ${
                      isDark ? "text-white-50" : "text-muted"
                    }`}
                  >
                    No activity to display
                  </div>
                </div>
              ) : (
                activityDates.map((date) => (
                  <div key={date} className="mb-3">
                    <div className="dash-date-sep">{date}</div>

                    {activities
                      .filter((a) => {
                        const label = new Date(a.created_at).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        );
                        return label === date;
                      })
                      .map((a, i) => (
                        <div
                          key={i}
                          className="dash-activity"
                          style={{ "--src": getBorderColor(a.source) }}
                        >
                          <Avatar name={a.name} size={38} />

                          <div className="dash-activity-main">
                            <div className="dash-activity-top">
                              <span className="dash-activity-name">{a.name}</span>
                              <FaCheckCircle className="dash-activity-check" title="Email sent" />
                            </div>

                            <div className="dash-activity-course">
                              <MdOutlineClass />
                              <span>{a.course}</span>
                            </div>

                            <div className="dash-activity-meta">
                              <FaRegUser />
                              <span>
                                {a.created_by} ·{" "}
                                {new Date(a.created_at).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
